import type { AppointmentStatus } from '../types/appointments.ts';
import type { Settings, CompletedMessageMode } from '../types/settings.ts';
import { getCurrencySymbol } from './currency.ts';
import { useAuthStore } from '../stores/auth.store.ts';

/**
 * Standard canonical tags supported across appointments, CRM and campaigns.
 */
export interface TemplateTagContext {
  // Client identification
  cliente?: string;
  nombre?: string;
  primer_nombre?: string;
  nombre_completo?: string;
  apellido?: string;

  // Business & Staff
  negocio?: string;
  profesional?: string;
  estilista?: string;
  sede?: string;
  ultima_sede?: string;

  // Appointment & Lifecycle
  fecha?: string;
  hora?: string;
  fecha_hora?: string;
  servicios?: string;
  de_servicios?: string;
  servicio?: string;
  ultimo_servicio?: string;
  ultima_cita?: string;

  // Pricing & Currency
  precio_bloque?: string;
  precio?: string;
  monto?: string;
  monto_bs?: string;
  precio_bs?: string;

  // History & Metrics (CRM & Campaigns)
  dias?: string;
  dias_ausente?: string;
  citas?: string;
  total_citas?: string;
  consumo?: string;
  total_ventas?: string;
  monto_ventas?: string;
  deuda?: string;
  monto_deuda?: string;
  cumpleanos?: string;
  dias_cumpleanos?: string;
  antiguedad?: string;
  tiempo_cliente?: string;

  // General & Styling
  saludo?: string;
  firma?: string;
  cierre?: string;
  link_resena?: string;
  enlace_resena?: string;
  extra?: string;

  // Arbitrary string tags
  [key: string]: string | undefined;
}

/**
 * Map of historical aliases to canonical tag names.
 */
const TAG_ALIASES: Record<string, string[]> = {
  cliente: ['nombre', 'client'],
  primer_nombre: ['first_name'],
  nombre_completo: ['full_name'],
  apellido: ['last_name'],
  negocio: ['business', 'empresa'],
  firma: ['cierre', 'closing'],
  link_resena: ['enlace_resena', 'review_link', 'link_review'],
  profesional: ['estilista', 'staff'],
  fecha_hora: ['datetime'],
  fecha: ['date'],
  hora: ['time'],
  servicios: ['servicio', 'service'],
  de_servicios: ['of_services'],
  precio_bloque: ['price_block'],
  monto: ['amount'],
  monto_total: ['precio_total', 'total_amount'],
  monto_bs: ['precio_bs', 'amount_bs'],
  dias: ['dias_ausente', 'days_absent'],
  total_citas: ['citas', 'visits'],
  total_ventas: ['consumo', 'total_sales'],
  deuda: ['saldo_deudor', 'debt'],
  deuda_bs: ['debt_bs', 'saldo_deudor_bs'],
  consumo_bs: ['total_sales_bs', 'ventas_bs'],
  cumpleanos: ['birthday'],
  antiguedad: ['tenure', 'tiempo_cliente'],
  ultimo_servicio: ['last_service'],
  ultima_cita: ['last_visit'],
  ultima_sede: ['sede', 'location', 'last_location'],
};

/**
 * Replace tags in a template string safely with case-insensitivity and alias resolution.
 */
export function interpolateTemplate(
  template: string,
  tags: Record<string, string | undefined>
): string {
  if (!template) return '';

  // Build normalized lookup dictionary (lowercase keys)
  const normalizedTags: Record<string, string> = {};
  for (const [key, value] of Object.entries(tags)) {
    if (value !== undefined && value !== null) {
      normalizedTags[key.toLowerCase()] = String(value);
    }
  }

  // Populate aliases
  for (const [canonical, aliases] of Object.entries(TAG_ALIASES)) {
    const val = normalizedTags[canonical];
    if (val !== undefined) {
      for (const alias of aliases) {
        if (normalizedTags[alias] === undefined) {
          normalizedTags[alias] = val;
        }
      }
    } else {
      // Check if any alias has a value
      for (const alias of aliases) {
        if (normalizedTags[alias] !== undefined) {
          normalizedTags[canonical] = normalizedTags[alias];
          break;
        }
      }
    }
  }

  // Perform case-insensitive tag replacement
  let result = template.replace(/\{([a-zA-Z0-9_-]+)\}/gi, (_match, rawKey) => {
    const key = rawKey.toLowerCase();
    return normalizedTags[key] !== undefined ? normalizedTags[key] : '';
  });

  // Clean up formatting artifacts (empty asterisks, empty parens, orphan colons) and whitespace
  return result
    .replace(/\*\s*\*/g, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\(\s*\*\*\s*\)/g, '')
    .replace(/([?!.,])\s*:\s*(?=\n|$)/g, '$1')
    .replace(/[ \t]+/g, ' ')
    .replace(/[ \t]+([.,:;!?])/g, '$1')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n\s*\n\s*\n+/g, '\n\n')
    .trim();
}

/**
 * Get effective business name from settings override or auth store (Fresha provider).
 */
export function getEffectiveBusinessName(settings: Settings): string {
  if (settings.businessName && settings.businessName.trim()) {
    return settings.businessName.trim();
  }
  const authState = useAuthStore.getState();
  return (
    authState.freshaProvider ||
    authState.provider?.data?.attributes?.name ||
    'Tu Centro / Negocio'
  );
}

/**
 * Format client display name respecting customerNameMode and nameMode.
 */
export function formatClientDisplayName(
  rawName = '',
  settings: Settings,
  firstName?: string,
  lastName?: string
): string {
  if (settings.customerNameMode === 'alias' && settings.customerAlias) {
    return settings.customerAlias;
  }

  if (settings.nameMode === 'first') {
    if (firstName) return firstName;
    const parts = rawName.trim().split(/\s+/);
    return parts[0] || 'Cliente';
  }

  if (rawName.trim()) return rawName.trim();
  if (firstName || lastName) return `${firstName || ''} ${lastName || ''}`.trim();
  return 'Cliente';
}

/**
 * Generate contextual greeting based on current time or plain prefix.
 */
export function getContextualGreeting(
  mode: 'dynamic' | 'plain' = 'dynamic',
  prefix = 'Hola'
): string {
  if (mode === 'plain') return prefix;
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'Buenos días';
  if (hour >= 12 && hour < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

/**
 * Resolve appointment template from status, completed strategy, and settings.
 */
export function resolveAppointmentTemplate(
  status: AppointmentStatus,
  strategy: CompletedMessageMode,
  settings: Settings,
  hasReview = false
): string {
  switch (status) {
    case 'new':
      return settings.templateNew;
    case 'confirmed':
    case 'unconfirmed':
      return settings.templateReminder;
    case 'arrived':
      return settings.templateArrived || settings.templateReminder;
    case 'started':
      return settings.templateStarted || settings.templateReminder;
    case 'completed':
      if (strategy === 'auto') {
        return hasReview ? settings.templateCompletedGeneral : settings.templateCompletedReview;
      }
      if (strategy === 'review_request') return settings.templateCompletedReview;
      if (strategy === 'rebook') return settings.templateCompletedRebook;
      if (strategy === 'loyalty') return settings.templateCompletedGeneral;
      return settings.templateCompletedGeneral || settings.templateCompleted;
    case 'no-show':
      return settings.templateNoShow;
    case 'cancelled':
      return settings.templateCancelled;
    default:
      return settings.templateReminder;
  }
}

/**
 * Generate a realistic preview of a template string for UI display.
 */
export function generateTemplatePreview(
  template: string,
  _type: 'appointment' | 'crm' | 'campaign',
  settings: Settings
): string {
  const sym = getCurrencySymbol(settings.currencyCode);
  const activeRate = settings.manualRate || 36.5;
  const sampleBs = (25 * activeRate).toFixed(2);

  const sampleTags: Record<string, string> = {
    saludo: 'Hola, buenas tardes',
    cliente: settings.customerNameMode === 'alias' ? settings.customerAlias : 'María',
    nombre: settings.customerNameMode === 'alias' ? settings.customerAlias : 'María',
    primer_nombre: 'María',
    apellido: 'Pérez',
    nombre_completo: 'María Pérez',
    negocio: getEffectiveBusinessName(settings),
    profesional: 'Ana Lucía',
    estilista: 'Ana Lucía',
    fecha: 'miércoles 19 de agosto',
    hora: 'a las 3:30 PM',
    fecha_hora: 'miércoles 19 de agosto a las 3:30 PM',
    para_fecha_hora: 'para el miércoles 19 de agosto a las 3:30 PM',
    el_fecha_hora: 'el miércoles 19 de agosto a las 3:30 PM',
    servicios: 'Corte + Secado',
    de_servicios: 'de Corte + Secado',
    servicio: 'Corte + Secado',
    ultimo_servicio: 'Corte + Secado',
    ultima_cita: 'hace 2 semanas',
    ultima_sede: 'Sede Principal',
    sede: 'Sede Principal',
    precio_bloque: `\n\nEl total es ${sym}25.00 (Bs. ${sampleBs}).`,
    precio: `El total es ${sym}25.00 (Bs. ${sampleBs}).`,
    monto: '25.00',
    monto_total: `${sym}25.00`,
    precio_total: `${sym}25.00`,
    monto_bs: sampleBs,
    precio_bs: sampleBs,
    dias: '45',
    dias_ausente: '45',
    citas: '8',
    total_citas: '8',
    consumo: `${sym}250.00`,
    consumo_bs: `Bs. ${(250 * activeRate).toFixed(2)}`,
    total_ventas: `${sym}250.00`,
    monto_ventas: '250.00',
    deuda: `${sym}15.00`,
    deuda_bs: `Bs. ${(15 * activeRate).toFixed(2)}`,
    monto_deuda: '15.00',
    cumpleanos: '15 de mayo',
    dias_cumpleanos: 'en 5 días',
    antiguedad: 'Cliente desde hace 2 años',
    tiempo_cliente: 'Cliente desde hace 2 años',
    firma: settings.closingText || '¡Te esperamos! Cualquier cambio nos avisas.',
    cierre: settings.closingText || '¡Te esperamos! Cualquier cambio nos avisas.',
    link_resena: settings.reviewLink?.trim() ? `👉 ${settings.reviewLink.trim()}` : '👉 https://g.page/r/fresha/review',
    enlace_resena: settings.reviewLink?.trim() ? `👉 ${settings.reviewLink.trim()}` : '👉 https://g.page/r/fresha/review',
    extra: '',
  };

  return interpolateTemplate(template, sampleTags);
}
